import { request } from '@umijs/max';
import type { RequestAnalyticsContext } from '@/analytics/context';
import type {
  PersonSearchRequest,
  SimplifiedPerson,
  SimplifiedPersonSearchResponse,
} from './model';
import type { PeopleUnlockCreditMetadata } from './sharedUnlock';

export async function searchPeople(
  params: PersonSearchRequest,
  analyticsContext?: RequestAnalyticsContext,
  options?: Record<string, any>,
): Promise<SimplifiedPersonSearchResponse> {
  return request('/people-v2/search', {
    ...options,
    analyticsContext,
    method: 'POST',
    data: params,
  });
}

export async function fetchPerson(
  personId: number,
  isFreePlan: boolean,
  analyticsContext?: RequestAnalyticsContext,
  options?: Record<string, any>,
): Promise<SimplifiedPerson> {
  return request(`/people-v2/${personId}/${isFreePlan.toString()}`, {
    ...options,
    analyticsContext,
    method: 'GET',
  });
}

export async function fetchPeopleListsV2(): Promise<any[]> {
  return request('/people-v2/list', {
    method: 'GET',
  });
}

export async function fetchPeopleListItemsV2(listId: number): Promise<any[]> {
  return request(`/people-v2/list-items/${listId}`);
}

export async function createPeopleListV2(data: { name: string }, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>): Promise<any> {
  return request('/people-v2/list', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function addPeopleToListV2(data: {
  listId: number;
  peopleIds: number[];
}, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>): Promise<any> {
  return request('/people-v2/addPeopleToList', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function fetchPeopleFromProductList(data: {
  product_list_item_ids: number[];
  look_for: string;
}, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>): Promise<any> {
  return request('/people-v2/fetch_people_from_product_list', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function fetchPeopleFromProductListBoth(data: {
  product_list_item_ids: number[];
}, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>): Promise<any> {
  return request('/people-v2/fetch_people_from_product_list_both', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function fetchOrganizationIndustries(query: string, page: number = 1): Promise<any> {
  return request(`/people-v2/organization-industries/${query}/${page}`, {
    method: 'GET',
  });
}

export async function fetchPersonSkills(query: string, page: number = 1): Promise<any> {
  return request(`/people-v2/skills/${query}/${page}`, {
    method: 'GET',
  });
}

export async function fetchPeopleFromProducts(data: { product_ids: number[]; look_for: string }, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>) {
  return request('/people-v2/fetch_people_from_products', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function fetchPeopleFromProductsBoth(data: { product_ids: number[] }, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>) {
  return request('/people-v2/fetch_people_from_products_both', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function fetchPhone(data: { person_id: number }, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>): Promise<
  PeopleUnlockCreditMetadata & {
    success: boolean;
    phone_numbers?: string[];
  }
> {
  return request('/people-v2/fetch_phone', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export async function fetchWorkEmail(data: { person_id: number }, analyticsContext?: RequestAnalyticsContext, options?: Record<string, any>): Promise<
  PeopleUnlockCreditMetadata & {
    success: boolean;
    person_id?: number;
    work_email?: string;
    work_email_status?: string;
    emails?: string[];
  }
> {
  return request('/people-v2/fetch_work_email', {
    ...options,
    analyticsContext,
    method: 'POST',
    data,
  });
}

export interface PeopleCrmExportResponse {
  success: boolean;
  provider: string;
  module: string;
  created: number;
  updated: number;
  linked: number;
  failed: number;
  results: Array<{
    name: string;
    action: 'created' | 'updated' | 'linked' | 'failed';
    error?: string;
  }>;
}

export async function sendPeopleToCrm(personIds: number[]): Promise<PeopleCrmExportResponse> {
  return request('/enterprise/people/crm', {
    method: 'POST',
    data: { person_ids: personIds },
  });
}
